import React, { useEffect, useState } from "react";
import { Button, Select, message } from "antd";
import { EyeOutlined, QrcodeOutlined, TeamOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/Tables/GenericTable";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import orderApproveIcon from "../../../Assets/Icons/order_approve.svg";
import creditClockIcon from "../../../Assets/Icons/credit_card_clock.svg";
import verifiedIcon from "../../../Assets/Icons/verified.svg";
import { useNavigate } from "react-router-dom";
import {
  getQRCodes,
  getQRFleetCompanies,
} from "../../../API/Admin/QRManagement";
import "./style.css";
import InitialsAvatar from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const QRManagement = () => {
  const navigate = useNavigate();
  const [qrData, setQrData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const fetchQRCodes = async (fleetCompanyId) => {
    try {
      const res = await getQRCodes(fleetCompanyId);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;
        const codes = Array.isArray(data?.qr_codes) ? data.qr_codes : [];

        setQrData(
          codes.map((item) => ({
            key: String(item.id),
            fleetManager: item.fleet_manager_name || "-",
            avatar: resolveAvatarUrl(item.fleet_manager_avatar),
            vehicles: item.vehicle_name || "-",
            plateNumber: item.license_plate || "-",
            cardNumber: item.card_number_masked || "-",
            status: item.status || "-",
          })),
        );
        setSummary(data?.summary || null);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to load QR codes."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load QR codes."),
      );
    }
  };

  useEffect(() => {
    fetchQRCodes(selectedCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const res = await getQRFleetCompanies();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setCompanyOptions(
            list.map((company) => ({
              value: company.id,
              label: company.legal_name || "-",
            })),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fleet companies."),
        );
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const totalCount = summary?.total?.value ?? qrData.length;
  const activeCount =
    summary?.active?.value ??
    qrData.filter((r) => r.status === "Active").length;
  const lockedCount =
    summary?.locked?.value ??
    qrData.filter((r) => r.status === "Fueling Locked").length;

  const columns = [
    {
      title: "Fleet Manager",
      key: "fleetManager",
      enableSearch: "true",
      render: (_, record) => (
        <div className="fleet-manager-cell">
          <InitialsAvatar
            src={record.avatar}
            name={record.fleetManager}
            className="fleet-avatar"
            style={{ fontSize: 11 }}
          />
          <span className="fleet-name">{record.fleetManager}</span>
        </div>
      ),
    },
    {
      title: "Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      enableSearch: "true",
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      enableSearch: "true",
    },
    {
      title: "QR",
      key: "qr",
      render: () => (
        <div className="qr-thumbnail">
          <QrcodeOutlined style={{ fontSize: 36, color: "#111" }} />
        </div>
      ),
    },
    {
      title: "Card Number",
      dataIndex: "cardNumber",
      key: "cardNumber",
      enableSearch: "true",
      render: (masked) => <span className="card-number">{masked}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Active", value: "Active" },
        { text: "Fueling Locked", value: "Fueling Locked" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`status-badge ${status === "Active" ? "active" : "locked"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined style={{ fontSize: 18, color: "#4F46E5" }} />}
          onClick={() =>
            navigate("/qr-active-detail", { state: { qr: record } })
          }
        />
      ),
    },
  ];

  return (
    <div className="qr-management-page">
      <div className="qr-management-page__stats-row">
        <div className="qr-management-page__stat-wrap">
          <GenericStatCard
            title="Total QR Codes"
            value={String(totalCount)}
            {...(summary?.total?.percentage != null && {
              percentage: `${summary.total.percentage}`,
            })}
            isIncrease={summary?.total?.is_increase}
            subtitle="vs. last month"
            icon={orderApproveIcon}
          />
        </div>
        <div className="qr-management-page__stat-wrap">
          <GenericStatCard
            title="Active QR Codes"
            value={String(activeCount)}
            {...(summary?.active?.percentage != null && {
              percentage: `${summary.active.percentage}`,
            })}
            isIncrease={summary?.active?.is_increase}
            subtitle="vs. last month"
            icon={verifiedIcon}
          />
        </div>
        <div className="qr-management-page__stat-wrap">
          <GenericStatCard
            title="Locked QR Codes"
            value={String(lockedCount)}
            {...(summary?.locked?.percentage != null && {
              percentage: `${summary.locked.percentage}`,
            })}
            isIncrease={summary?.locked?.is_increase}
            subtitle="vs. last month"
            icon={creditClockIcon}
          />
        </div>
      </div>

      <div className="qr-management-page__filter">
        <div className="qr-management-page__filter-text">
          <span className="qr-management-page__filter-title">
            Filter by Fleet Manager
          </span>
          <span className="qr-management-page__filter-hint">
            Show QR stickers for a single fleet
          </span>
        </div>
        <Select
          prefix={<TeamOutlined className="qr-management-page__filter-icon" />}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select Fleet Manager"
          value={selectedCompany}
          onChange={(value) => setSelectedCompany(value ?? null)}
          options={companyOptions}
          loading={companiesLoading}
          className="qr-management-page__company-select"
        />
      </div>

      <GenericTable
        title="QR Sticker"
        dataSource={qrData}
        columns={columns}
        enableRowSelection={false}
        headerButtonText="Fuel Cards"
        onHeaderButtonClick={() => navigate("/fuel-cards")}
      />
    </div>
  );
};

export default QRManagement;
